// v222 — Build-Info browse API client (item 33 of
// docs/PRODUCT-ROADMAP.md).
//
// Phase A: project-scoped browse:
//   GET /api/v1/projects/{id}/build-info?name=&since=&limit=  list
//   GET /api/v1/projects/{id}/build-info/{name}/{number}      detail
//
// Phase B: promotion + provenance:
//   POST /api/v1/projects/{id}/build-info/{name}/{number}/promote
//   GET  /api/v1/artifact-builds/{artifactId}
//
// CI publish stays on /api/build (handlers/buildinfo.go); the daily-
// driver UI only consumes the new project-scoped surfaces.

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export interface BuildSummary {
  id: string;
  name: string;
  number: string;
  started: string;
  vcs_revision?: string;
  vcs_url?: string;
  agent_name?: string;
  agent_version?: string;
  artifacts_count: number;
  created_at: string;
}

export interface BuildArtifact {
  module_id: string;
  artifact_name: string;
  artifact_type?: string;
  sha256?: string;
}

// v224 (item 33 Phase C) — denormalised dependency rows. Surface
// the JFrog `module.dependencies[]` shape without the caller having
// to parse the raw body JSONB.
export interface BuildDependency {
  module_id: string;
  dep_id: string;
  scope?: string;
  dep_type?: string;
  sha256?: string;
}

export interface BuildDetail extends BuildSummary {
  properties: Record<string, string>;
  artifacts: BuildArtifact[];
  dependencies: BuildDependency[];
  body: unknown;
}

export interface BuildListResponse {
  builds: BuildSummary[];
}

const fetcher = async (url: string) => {
  const r = await fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

export interface UseProjectBuildsOpts {
  name?: string;
  since?: string;
  limit?: number;
}

function projectBuildsUrl(projectId: string, qs: string): string {
  const suffix = qs ? `?${qs}` : '';
  return `/api/v1/projects/${encodeURIComponent(projectId)}/build-info${suffix}`;
}

export function useProjectBuilds(projectId?: string, opts: UseProjectBuildsOpts = {}) {
  const params = new URLSearchParams();
  if (opts.name) params.set('name', opts.name);
  if (opts.since) params.set('since', opts.since);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const url = projectId ? projectBuildsUrl(projectId, qs) : null;
  return useSWR<BuildListResponse>(url, fetcher);
}

export function useBuildDetail(projectId?: string, name?: string, number?: string) {
  const url =
    projectId && name && number
      ? `/api/v1/projects/${encodeURIComponent(projectId)}/build-info/${encodeURIComponent(name)}/${encodeURIComponent(number)}`
      : null;
  return useSWR<BuildDetail>(url, fetcher);
}

// --- Phase B: promotion-by-build ---

export interface PromoteRequest {
  target_repo_id: string;
  require_approval?: boolean;
  note?: string;
}

export interface PromoteSummary {
  id: string;
  src_repo_id: string;
  src_slug: string;
  dst_repo_id: string;
  dst_slug: string;
  artifact_count: number;
  status: string;
}

export interface PromoteResponse {
  promotions: PromoteSummary[];
  total_artifacts: number;
  skipped_not_in_registry: number;
  skipped_already_in_target: number;
}

export async function promoteBuild(
  projectId: string,
  name: string,
  number: string,
  body: PromoteRequest,
): Promise<PromoteResponse> {
  const res = await fetch(
    `/api/v1/projects/${encodeURIComponent(projectId)}/build-info/${encodeURIComponent(name)}/${encodeURIComponent(number)}/promote`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json() as Promise<PromoteResponse>;
}

// --- Phase B: per-artifact provenance ---

export interface ArtifactBuildSummary {
  build_id: string;
  project_id?: string;
  project_slug?: string;
  build_name: string;
  build_number: string;
  started: string;
  vcs_url?: string;
  vcs_revision?: string;
  // Item 73 Phase B — extracted from JFrog Build-Info body JSONB on
  // read. Branch / build_url / principal aren't normalised columns
  // because not every CI emitter populates them; the backend
  // COALESCEs across known shapes (`body.vcs.branch`,
  // `body.vcsBranch`; `body.buildUrl`, `body.url`).
  vcs_branch?: string;
  agent_name?: string;
  agent_version?: string;
  build_url?: string;
  principal?: string;
  module_id: string;
  artifact_name: string;
}

export interface ArtifactBuildsResponse {
  builds: ArtifactBuildSummary[];
}

export function useArtifactBuilds(artifactId?: string) {
  const url = artifactId
    ? `/api/v1/artifact-builds/${encodeURIComponent(artifactId)}`
    : null;
  return useSWR<ArtifactBuildsResponse>(url, fetcher);
}

// --- Phase C (v224): reverse dependency search ---
//
// "Which builds in this project consumed log4j-2.14.1?" Hits the
// /dependency-search endpoint on the project-scoped surface. Mode
// defaults to coord (exact-match coordinate lookup); prefix and
// sha256 modes are also supported.
//
// Item 66 (build-info Phase C polish) — adds the symmetric
// producer-side answer "which builds in this project PRODUCED
// log4j-2.14.1?" via the new role parameter. Each hit carries a
// `role` discriminator so role=any can render both halves in one
// list, and produced-side hits include artifact_id / repo_id /
// artifact_path when the produced sha256 also lives in the
// registry-side `artifacts` table — the UI deep-links those into
// the artifact-detail page so a SOC operator can pivot from CVE
// hit → upstream build → registry artifact in one click.

export type DepSearchMode = 'coord' | 'prefix' | 'sha256';
export type DepSearchRole = 'consumed' | 'produced' | 'any';

export interface DepSearchHit {
  build_id: string;
  build_name: string;
  build_number: string;
  started: string;
  vcs_url?: string;
  vcs_revision?: string;
  module_id: string;
  role: 'consumed' | 'produced';
  dep_id: string;
  dep_scope?: string;
  dep_type?: string;
  sha256?: string;
  // Populated for role=produced hits (item 66).
  artifact_name?: string;
  artifact_id?: string;
  repo_id?: string;
  artifact_path?: string;
}

export interface DepSearchResponse {
  hits: DepSearchHit[];
  mode: DepSearchMode;
  role: DepSearchRole;
  query: string;
}

export function useBuildDependencySearch(
  projectId?: string,
  query?: string,
  mode: DepSearchMode = 'coord',
  role: DepSearchRole = 'consumed',
) {
  const trimmed = (query ?? '').trim();
  const params = new URLSearchParams();
  params.set('q', trimmed);
  params.set('mode', mode);
  params.set('role', role);
  const url =
    projectId && trimmed
      ? `/api/v1/projects/${encodeURIComponent(projectId)}/build-info/dependency-search?${params.toString()}`
      : null;
  return useSWR<DepSearchResponse>(url, fetcher);
}

// buildSBOMUrl returns the CycloneDX 1.5 download URL for a build.
// We don't expose this through SWR — it's a download-on-click action,
// not a page-render fetch. Browser navigation honours the cookie auth
// the rest of the app uses, so a plain anchor href is enough.
export function buildSBOMUrl(
  projectId: string,
  name: string,
  number: string,
): string {
  return `/api/v1/projects/${encodeURIComponent(projectId)}/build-info/${encodeURIComponent(name)}/${encodeURIComponent(number)}/sbom`;
}
